// src/utils/geoLocation.js
//
// Lightweight, privacy-friendly, client-side "which US state is this user
// probably in" detection. This is an OPTIONAL ENHANCEMENT ONLY — it never
// blocks page load, never re-prompts once the user has responded, and its
// result is never treated as authoritative. The existing manual
// state-selection system (Navbar dropdown / PreviewGate) always remains the
// source of truth the user can confirm or override.
//
// Design notes:
//  - Uses the standard browser Geolocation API (navigator.geolocation),
//    which requires explicit user permission — no location data is ever
//    sent to a third-party service. Coordinates are matched against a local
//    table of approximate US state bounding boxes entirely in the browser.
//  - Because state borders aren't rectangles, bounding-box matching is
//    inherently approximate (this is acceptable since it's a fallback UX
//    convenience, not a legal/compliance determination).

// Approximate lat/lon bounding boxes for the 50 states + DC.
// [minLat, maxLat, minLon, maxLon]
const STATE_BOUNDING_BOXES = {
  AL: [30.2, 35.0, -88.5, -84.9],
  AK: [51.2, 71.4, -179.2, -129.9],
  AZ: [31.3, 37.0, -114.8, -109.0],
  AR: [33.0, 36.5, -94.6, -89.6],
  CA: [32.5, 42.0, -124.4, -114.1],
  CO: [37.0, 41.0, -109.1, -102.0],
  CT: [41.0, 42.1, -73.8, -71.8],
  DE: [38.4, 39.9, -75.8, -75.0],
  DC: [38.79, 39.0, -77.12, -76.90],
  FL: [24.4, 31.0, -87.7, -80.0],
  GA: [30.4, 35.0, -85.7, -80.8],
  HI: [18.9, 22.3, -160.3, -154.8],
  ID: [42.0, 49.0, -117.3, -111.0],
  IL: [36.9, 42.55, -91.6, -87.0],
  IN: [37.75, 41.8, -88.1, -84.7],
  IA: [40.35, 43.55, -96.7, -90.1],
  KS: [37.0, 40.0, -102.1, -94.6],
  KY: [36.5, 39.15, -89.6, -81.9],
  LA: [28.9, 33.0, -94.1, -88.8],
  ME: [43.0, 47.5, -71.1, -66.9],
  MD: [37.9, 39.7, -79.5, -75.0],
  MA: [41.2, 42.9, -73.5, -69.9],
  MI: [41.7, 48.3, -90.5, -82.4],
  MN: [43.5, 49.4, -97.3, -89.5],
  MS: [30.1, 35.0, -91.7, -88.1],
  MO: [36.0, 40.6, -95.8, -89.1],
  MT: [44.35, 49.0, -116.1, -104.0],
  NE: [40.0, 43.0, -104.1, -95.3],
  NV: [35.0, 42.0, -120.0, -114.0],
  NH: [42.7, 45.3, -72.6, -70.6],
  NJ: [38.9, 41.4, -75.6, -73.9],
  NM: [31.3, 37.0, -109.1, -103.0],
  NY: [40.5, 45.0, -79.8, -71.9],
  NC: [33.8, 36.6, -84.3, -75.4],
  ND: [45.9, 49.0, -104.1, -96.6],
  OH: [38.4, 42.0, -84.9, -80.5],
  OK: [33.6, 37.0, -103.0, -94.4],
  OR: [41.9, 46.3, -124.6, -116.5],
  PA: [39.7, 42.3, -80.6, -74.7],
  RI: [41.1, 42.05, -71.9, -71.1],
  SC: [32.0, 35.2, -83.4, -78.5],
  SD: [42.5, 45.95, -104.1, -96.4],
  TN: [34.9, 36.7, -90.4, -81.6],
  TX: [25.8, 36.5, -106.7, -93.5],
  UT: [37.0, 42.0, -114.1, -109.0],
  VT: [42.7, 45.05, -73.5, -71.5],
  VA: [36.5, 39.5, -83.7, -75.2],
  WA: [45.5, 49.0, -124.8, -116.9],
  WV: [37.2, 40.65, -82.7, -77.7],
  WI: [42.4, 47.1, -92.9, -86.8],
  WY: [41.0, 45.0, -111.1, -104.0],
};

// Matches coordinates against every candidate bounding box and returns the
// smallest (most specific) match, which resolves most overlap ambiguity
// caused by non-rectangular state borders (e.g. DC inside MD/VA, RI inside
// MA/CT).
export function coordsToStateCode(lat, lon) {
  if (typeof lat !== 'number' || typeof lon !== 'number' || Number.isNaN(lat) || Number.isNaN(lon)) {
    return null;
  }

  let bestCode = null;
  let bestArea = Infinity;

  for (const [code, [minLat, maxLat, minLon, maxLon]] of Object.entries(STATE_BOUNDING_BOXES)) {
    if (lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon) {
      const area = (maxLat - minLat) * (maxLon - minLon);
      if (area < bestArea) {
        bestArea = area;
        bestCode = code;
      }
    }
  }

  return bestCode;
}

const GEO_ATTEMPTED_KEY = 'geoStateDetectionAttempted';

// Whether we should even try geolocation: only if the browser supports it,
// and only if we haven't already attempted it before (successful or not) —
// this is what prevents repeatedly prompting the user for permission across
// visits.
export function hasGeoDetectionBeenAttempted() {
  try {
    return localStorage.getItem(GEO_ATTEMPTED_KEY) === 'true';
  } catch {
    return true; // if storage is unavailable, err on the side of not asking
  }
}

function markGeoDetectionAttempted() {
  try {
    localStorage.setItem(GEO_ATTEMPTED_KEY, 'true');
  } catch {
    // ignore — non-critical
  }
}

// Resolves to a 2-letter state code, or null if location is unavailable,
// permission is denied/not-yet-granted, detection times out, or the
// coordinates don't fall within any known state box. Never rejects, never
// throws, and never blocks the caller for longer than `timeoutMs`.
export function detectStateFromLocation({ timeoutMs = 6000 } = {}) {
  markGeoDetectionAttempted();

  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      resolve(null);
      return;
    }

    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    // Safety-net timer in case the browser never calls back at all.
    const safetyTimer = setTimeout(() => finish(null), timeoutMs + 2000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(safetyTimer);
        const { latitude, longitude } = position.coords || {};
        finish(coordsToStateCode(latitude, longitude));
      },
      () => {
        // Permission denied, position unavailable, or timed out — silently
        // fall back, no error surfaced to the user.
        clearTimeout(safetyTimer);
        finish(null);
      },
      {
        enableHighAccuracy: false,
        timeout: timeoutMs,
        maximumAge: 10 * 60 * 1000, // reuse a recent cached position if available
      }
    );
  });
}

// Optional pre-check so we can skip even attempting `getCurrentPosition`
// when the Permissions API reports the user has already denied access.
// Not all browsers support the 'geolocation' permission name (notably
// Safari), so this fails open (returns 'unknown') rather than blocking.
export async function getGeoPermissionState() {
  try {
    if (!navigator.permissions || !navigator.permissions.query) return 'unknown';
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state; // 'granted' | 'denied' | 'prompt'
  } catch {
    return 'unknown';
  }
}
